/**
 * PortalSidebar Component
 * 사용자 포탈용 2단 사이드바 (아이콘 패널 + 서브메뉴 패널)
 */

import { useNavigate } from 'react-router-dom';
import { authApi, Workflow } from '../../api';

// 녹색 그라데이션 (사용자 포탈용)
const GREEN_GRADIENT = 'linear-gradient(155deg, #4ade80 0%, #16a34a 100%)';

// 아이콘 컴포넌트 - 녹색 기본 색상 (사용자 포탈용)
const IconPart = ({ left, top, width, height, style, active }: { 
  left: number; top: number; width: number; height: number; style?: React.CSSProperties; active?: boolean;
}) => (
  <span style={{
    position: 'absolute',
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    height: `${height}px`,
    background: active 
      ? '#fff' 
      : GREEN_GRADIENT,
    boxShadow: active 
      ? 'none' 
      : 'inset 0 1.5px 1px rgba(255,255,255,0.5), inset 0 -2px 2px rgba(0,0,30,0.4)',
    ...style
  }} />
);

const Icons: Record<string, ({ active }: { active?: boolean }) => JSX.Element> = {
  // 홈 아이콘
  home: ({ active }: { active?: boolean }) => (
    <div className="icon-wrap">
      <IconPart left={3} top={8} width={14} height={10} style={{ borderRadius: '2px' }} active={active} />
      <IconPart left={6} top={2} width={8} height={8} style={{ clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} active={active} />
    </div>
  ),
  // 챗봇 아이콘
  chatbot: ({ active }: { active?: boolean }) => (
    <div className="icon-wrap">
      <IconPart left={2} top={3} width={16} height={11} style={{ borderRadius: '5px' }} active={active} />
      <IconPart left={5} top={12} width={5} height={5} style={{ clipPath: 'polygon(0 0,100% 0,0 100%)' }} active={active} />
    </div>
  ),
  // 워크플로우 - 연결된 원
  workflows: ({ active }: { active?: boolean }) => (
    <div className="icon-wrap">
      <IconPart left={1} top={4} width={7} height={7} style={{ borderRadius: '50%' }} active={active} />
      <IconPart left={5} top={6.5} width={10} height={2} style={{ borderRadius: '1px' }} active={active} />
      <IconPart left={12} top={4} width={7} height={7} style={{ borderRadius: '50%' }} active={active} />
      <IconPart left={14.5} top={10} width={2} height={5} active={active} />
      <IconPart left={11} top={12} width={7} height={7} style={{ borderRadius: '50%' }} active={active} />
    </div>
  ),
  // 마켓플레이스 - 상점
  marketplace: ({ active }: { active?: boolean }) => (
    <div className="icon-wrap" style={{ transform: 'scale(1.25)' }}>
      <span style={{ position: 'absolute', left: '1px', top: '1px', width: '18px', height: '6px', borderRadius: '3px 3px 0 0', background: active ? '#fff' : GREEN_GRADIENT }} />
      <IconPart left={3} top={9} width={14} height={10} style={{ borderRadius: '0 0 3px 3px' }} active={active} />
      <span style={{ position: 'absolute', left: '7px', top: '12px', width: '6px', height: '7px', borderRadius: '3px 3px 0 0', background: active ? 'rgba(200,255,220,0.9)' : '#e8f7ee' }} />
    </div>
  ),
  // 템플릿 - 3장 카드
  templates: ({ active }: { active?: boolean }) => (
    <div className="icon-wrap">
      <span style={{ position: 'absolute', left: '5px', top: '1px', width: '12px', height: '15px', borderRadius: '3px', background: active ? 'rgba(255,255,255,0.4)' : 'rgba(74,222,128,0.4)' }} />
      <span style={{ position: 'absolute', left: '3px', top: '3px', width: '12px', height: '15px', borderRadius: '3px', background: active ? 'rgba(255,255,255,0.65)' : 'rgba(74,222,128,0.7)' }} />
      <span style={{ position: 'absolute', left: '1px', top: '5px', width: '12px', height: '15px', borderRadius: '3px', background: active ? '#fff' : GREEN_GRADIENT }} />
    </div>
  ),
  // 지식 파이프라인 - 폴더
  knowledge: ({ active }: { active?: boolean }) => (
    <div className="icon-wrap" style={{ transform: 'scale(1.7)' }}>
      <span style={{ position: 'absolute', left: '2px', top: '6px', width: '8px', height: '3px', borderRadius: '2px 5px 0 0', opacity: 0.85, background: active ? 'rgba(255,255,255,0.95)' : GREEN_GRADIENT }} />
      <span style={{ position: 'absolute', left: '2px', top: '8px', width: '16px', height: '10px', borderRadius: '0 3px 3px 3px', background: active ? 'rgba(255,255,255,0.95)' : GREEN_GRADIENT }} />
    </div>
  ),
};

type PortalView = 'home' | 'agents' | 'workflows' | 'marketplace' | 'templates' | 'knowledge';

interface RecentChat {
  workflowId: string;
  workflowName: string;
}

interface PortalSidebarProps {
  activeView: PortalView;
  onViewChange: (view: PortalView) => void;
  recentChats: RecentChat[];
  chatbots: Workflow[];
  onSelectChatbot: (workflow: Workflow | null) => void;
  selectedChatbot: Workflow | null;
}

const navItems = [
  { key: 'home' as PortalView, label: '홈' },
  { key: 'agents' as PortalView, label: '챗봇' },
  { key: 'workflows' as PortalView, label: '워크플로우' },
  { key: 'marketplace' as PortalView, label: '마켓플레이스' },
  { key: 'templates' as PortalView, label: '템플릿' },
  { key: 'knowledge' as PortalView, label: '지식 파이프라인' },
];

export function PortalSidebar({ activeView, onViewChange, recentChats, chatbots, onSelectChatbot, selectedChatbot }: PortalSidebarProps) {
  const navigate = useNavigate();
  const user = authApi.getUser();

  const handleLogout = () => {
    authApi.logout();
    navigate('/login');
  };

  const handleIconClick = (view: PortalView) => {
    // 외부 경로는 navigate로 이동
    if (view === 'workflows') { navigate('/portal/workflows'); return; }
    if (view === 'marketplace') { navigate('/portal/marketplace'); return; }
    if (view === 'templates') { navigate('/portal/templates'); return; }
    if (view === 'knowledge') { navigate('/portal/knowledge'); return; }
    onViewChange(view);
  };

  // 현재 뷰에 따른 서브메뉴 타이틀
  const getSubPanelTitle = () => {
    switch (activeView) {
      case 'home': return '홈';
      case 'agents': return '챗봇';
      case 'workflows': return '워크플로우';
      case 'marketplace': return '마켓플레이스';
      case 'templates': return '템플릿';
      case 'knowledge': return '지식 파이프라인';
      default: return '';
    }
  };

  return (
    <aside className="sidebar-wrapper">
      {/* 아이콘 패널 (1뎁스) */}
      <div className="sidebar-icon-panel">
        <div className="icon-panel-brand">
          <div className="bot-logo">
            <span className="bot-face" style={{ background: 'linear-gradient(155deg, #4ade80 0%, #16a34a 100%)' }}></span>
            <span className="bot-leg bot-leg-l" style={{ background: 'linear-gradient(155deg, #4ade80 0%, #16a34a 100%)' }}></span>
            <span className="bot-leg bot-leg-r" style={{ background: 'linear-gradient(155deg, #4ade80 0%, #16a34a 100%)' }}></span>
            <span className="bot-eye bot-eye-l"></span>
            <span className="bot-eye bot-eye-r"></span>
          </div>
        </div>

        {/* 메인 메뉴 아이콘 */}
        <nav className="icon-panel-nav">
          {navItems.map((item) => {
            const IconComponent = Icons[item.key === 'agents' ? 'chatbot' : item.key];
            const isActive = activeView === item.key;
            return (
              <button
                key={item.key}
                className={`icon-btn ${isActive ? 'active' : ''}`}
                onClick={() => handleIconClick(item.key)}
                title={item.label}
              >
                <IconComponent active={isActive} />
              </button>
            );
          })}
        </nav>

        {/* 하단 사용자 아바타 + 로그아웃 */}
        <div className="icon-panel-bottom">
          <div className="avatar-btn avatar-btn-full" title={user?.name || user?.email || '사용자'}>
            {user?.name || (user?.email ? user.email.split('@')[0] : '사용자')}
          </div>
          <button 
            className="icon-btn logout-icon-btn" 
            onClick={handleLogout}
            title="로그아웃"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </div>


      {/* 서브메뉴 패널 (2뎁스) */}
      <div className="sidebar-sub-panel">
        {/* 상단: 로고와 제품명 */}
        <div className="sub-panel-brand">
          <div className="sub-panel-brand-logo" style={{ background: 'linear-gradient(145deg, #4ade80, #16a34a)' }}>AB</div>
          <div className="sub-panel-brand-info">
            <span className="sub-panel-brand-name">AgentBuilder</span>
            <span className="sub-panel-brand-desc">사용자 포탈</span>
          </div>
        </div>

        {/* 섹션 라벨 + 메뉴 타이틀 */}
        <div className="sub-panel-header">
          <span className="sub-panel-section">메뉴</span>
          <span className="sub-panel-title">{getSubPanelTitle()}</span>
        </div>

        {/* 서브메뉴 내용 */}
        <div className="sub-panel-content">
          {activeView === 'home' && (
            <button
              className="sub-panel-item active"
              onClick={() => onViewChange('home')}
            >
              <span className="sub-panel-item-label">홈 보기</span>
            </button>
          )}

          {activeView === 'agents' && (
            <>
              <button
                className={`sub-panel-item ${!selectedChatbot ? 'active' : ''}`}
                onClick={() => onSelectChatbot(null)}
              >
                <span className="sub-panel-item-label">전체</span>
              </button>
              {chatbots.map((bot) => (
                <button
                  key={bot.id}
                  className={`sub-panel-item ${selectedChatbot?.id === bot.id ? 'active' : ''}`}
                  onClick={() => onSelectChatbot(bot)}
                >
                  <span className="sub-panel-item-label">{bot.name}</span>
                </button>
              ))}
            </>
          )}

        </div>

        {/* 최근 대화 */}
        {recentChats.length > 0 && (
          <>
            <div className="sub-panel-section-label" style={{ marginTop: '16px' }}>최근 대화</div>
            <div className="sub-panel-content">
              {recentChats.map((chat) => (
                <button
                  key={chat.workflowId}
                  className="sub-panel-item"
                  onClick={() => {
                    const workflow = chatbots.find((c) => c.id === chat.workflowId);
                    if (workflow) onSelectChatbot(workflow);
                  }}
                >
                  <span className="sub-panel-item-label">{chat.workflowName}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* 사용자 정보 */}
        <div className="sub-panel-user">
          <div className="sub-panel-user-avatar">
            {(user?.name || user?.email || '사')[0].toUpperCase()}
          </div>
          <div className="sub-panel-user-info">
            <span className="sub-panel-user-name">{user?.name || (user?.email ? user.email.split('@')[0] : '사용자')}</span>
            <span className="sub-panel-user-email">{user?.email || ''}</span>
          </div>
        </div>
      </div>
    </aside>
  );
}

export default PortalSidebar;
